import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import ScoreGauge from "@/components/ScoreGauge";
import { TrendingUp, TrendingDown, Minus, Info as InfoIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const METRIC_META = [
  {
    key: "bug_quality",
    label: "Bug Quality",
    weight: 30,
    tip: "Percent of your submitted issues that were accepted (Valid) vs Rejected or Duplicate. Improve by adding clear repro steps, expected/actual, and screenshots.",
  },
  {
    key: "on_time_delivery",
    label: "On-Time Delivery",
    weight: 25,
    tip: "Share of completed assignments delivered on or before the due date. Improve by submitting before SLA — even a partial finding beats missing a deadline.",
  },
  {
    key: "client_feedback",
    label: "Client Feedback",
    weight: 25,
    tip: "Automated feedback signal derived from acceptance rate and re-work frequency. (Manager stars are no longer used.)",
  },
  {
    key: "responsiveness",
    label: "Responsiveness",
    weight: 10,
    tip: "How quickly you respond to task invitations. Currently uses a baseline of 70 and will move to fully computed values in a follow-up release.",
  },
  {
    key: "reliability",
    label: "Reliability",
    weight: 10,
    tip: "Share of task/project invitations you accepted vs declined or no-showed. Improve by declining early rather than ghosting.",
  },
];

function TrendPill({ delta }) {
  if (delta == null || Number.isNaN(delta)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="trend-pill">
        <Minus className="w-3.5 h-3.5" /> No prior period
      </span>
    );
  }
  const positive = delta > 0;
  const negative = delta < 0;
  const cls = positive
    ? "text-emerald-700"
    : negative
      ? "text-red-700"
      : "text-slate-600";
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const sign = positive ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium ${cls}`} data-testid="trend-pill">
      <Icon className="w-4 h-4" /> {sign}{delta} pts from last month
    </span>
  );
}

function MetricBar({ meta, value, muted }) {
  const val = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={`space-y-1.5 ${muted ? "opacity-40" : ""}`} data-testid={`metric-${meta.key}`}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-slate-900">{meta.label}</span>
          <TooltipProvider delayDuration={100}>
            <UITooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-slate-400 hover:text-slate-700" aria-label={`How ${meta.label} is calculated`}>
                  <InfoIcon className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs leading-snug">
                {meta.tip}
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
          <span className="pill bg-slate-100 text-slate-500 border-slate-200 text-[10px]">
            {meta.weight}% weight
          </span>
        </div>
        <span className="font-display font-bold text-slate-900">
          {muted ? <span className="text-slate-400">—</span> : <>{val.toFixed(1)}<span className="text-xs text-slate-500 font-normal">/100</span></>}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-cyan-600 transition-all" style={{ width: muted ? "0%" : `${val}%` }} />
      </div>
    </div>
  );
}

export default function Score() {
  const [s, setS] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/scoring/me");
      setS(data);
      const seed = data?.history || [];
      if (seed.length > 0) {
        setHistory(seed);
      } else {
        try {
          const r = await api.get("/scoring/me/history");
          setHistory(r.data?.history || []);
        } catch { /* ignore */ }
      }
    })();
  }, []);

  if (!s) return <AppLayout title="Score"><div>Loading…</div></AppLayout>;

  const m = s.metrics || {};
  const state = s.state || "unrated";
  const isUnrated = state === "unrated";
  const isProvisional = state === "provisional";
  const completed = s.completed_count ?? m.completed_tasks ?? 0;

  return (
    <AppLayout title="Tester Score" subtitle="Automated performance rating">
      {/* Top: Score card */}
      <div className="grid lg:grid-cols-3 gap-5" data-testid="score-top-card">
        <div className="kpi-card flex flex-col items-center py-8">
          <ScoreGauge score={s.score} tier={s.tier} state={state} size={220} />
          {!isUnrated && (
            <div className="mt-3 text-center">
              <TrendPill delta={s.trend_delta} />
              <div className="text-xs text-slate-500 mt-1" data-testid="score-basis">
                Based on {completed} completed assignment{completed === 1 ? "" : "s"}
                {isProvisional && " · Provisional"}
              </div>
            </div>
          )}
          {isUnrated && (
            <div className="mt-3 text-center max-w-xs" data-testid="score-empty-hint">
              <div className="text-sm font-medium text-slate-900">No rating yet</div>
              <div className="text-xs text-slate-500 mt-1">
                Complete your first assignment to get scored.
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 kpi-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold text-slate-900">Score breakdown</h3>
            {isProvisional && (
              <span className="pill bg-amber-50 text-amber-800 border-amber-200 text-[11px]" data-testid="provisional-hint">
                Score will stabilize after 5 assignments
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Your score is a weighted composite of five sub-metrics. Hover the ⓘ icons for tips on how to improve each one.
          </p>
          <div className="space-y-4" data-testid="metric-breakdown">
            {METRIC_META.map((meta) => (
              <MetricBar key={meta.key} meta={meta} value={m[meta.key]} muted={isUnrated} />
            ))}
          </div>
        </div>
      </div>

      {/* History */}
      <div className="kpi-card mt-5" data-testid="score-history-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-slate-900">Score history</h3>
          <span className="text-xs text-slate-500">Last 12 months</span>
        </div>
        {(!history || history.length < 2) ? (
          <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-md" data-testid="history-empty">
            Not enough data yet — your score history will populate here as you complete more assignments.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={history} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
              <YAxis domain={[0, 100]} stroke="#94A3B8" fontSize={11} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }}
                formatter={(value, name, ctx) => {
                  if (name === "score") return [`${value} / 100`, "Score"];
                  return [value, name];
                }}
                labelFormatter={(label, payload) => {
                  const p = payload && payload[0] && payload[0].payload;
                  return p ? `${label} · ${p.completed_count || 0} assignments` : label;
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="score" stroke="#0E7490" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tier thresholds */}
      <div className="kpi-card mt-5">
        <h3 className="font-display font-bold text-slate-900 mb-3">Tier thresholds</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm" data-testid="tier-thresholds">
          <TierCard label="Unrated" range="0 completed" desc="Entry-level / trial assignments only" color="bg-slate-100 text-slate-600 border-slate-200" />
          <TierCard label="Bronze" range="60–74" desc="Standard assignments" color="bg-amber-50 text-amber-800 border-amber-200" />
          <TierCard label="Silver" range="75–84" desc="Standard + mid-priority" color="bg-slate-100 text-slate-700 border-slate-300" />
          <TierCard label="Gold" range="85–92" desc="High-priority & specialized (AI / Payments / Accessibility / Performance)" color="bg-amber-100 text-amber-800 border-amber-300" />
          <TierCard label="Platinum" range="93–100" desc="Top-tier assignments & highest payouts" color="bg-cyan-900 text-cyan-100 border-cyan-700" />
        </div>
        <div className="text-[11px] text-slate-500 mt-3">
          Gold and Platinum tiers require at least 5 completed assignments — even if your raw score qualifies earlier.
        </div>
      </div>
    </AppLayout>
  );
}

function TierCard({ label, range, desc, color }) {
  return (
    <div className={`p-3 border rounded-md ${color}`} data-testid={`tier-card-${label.toLowerCase()}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{range}</div>
      <div className="font-display font-bold">{label}</div>
      <div className="text-[11px] mt-1 opacity-80 leading-snug">{desc}</div>
    </div>
  );
}
